// src/components/Screening/SortingStep.jsx
import React from 'react'
import { BarChart3, AlertTriangle } from 'lucide-react'
import GlowingButton from '../Common/GlowingButton'

const EDUCATION_LEVELS = {
  'bac': { title: 'Bac', level: 1, description: 'Baccalauréat' },
  'bac+2': { title: 'Bac+2', level: 2, description: 'BTS, DUT, DEUG' },
  'bac+3': { title: 'Bac+3', level: 3, description: 'Licence, Bachelor' },
  'bac+3+': { title: 'Supérieur à Bac+3', level: 4, description: 'Master, Ingénieur, Doctorat' }
}

const EDUCATION_FIELDS = {
  'informatique': 'Informatique & Numérique',
  'commerce': 'Commerce & Vente',
  'marketing': 'Marketing & Communication',
  'finance': 'Finance & Comptabilité',
  'rh': 'Ressources Humaines',
  'ingenierie': 'Ingénierie & Technique',
  'design': 'Design & Création',
  'juridique': 'Juridique & Droit',
  'sante': 'Santé & Médical',
  'education': 'Éducation & Formation',
  'autre': 'Autre domaine'
}

const GENDER_OPTIONS = {
  'homme': 'Homme',
  'femme': 'Femme',
  'indifferent': 'Indifférent'
}

const SortingStep = ({ criteria, onCriteriaChange, onNext, onPrev, errors = {} }) => {
  const ToggleSwitch = ({ checked, onChange, color = 'blue' }) => (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <div className={`w-12 h-6 rounded-full transition-colors ${
        checked ? `bg-${color}-500` : 'bg-gray-600'
      }`}>
        <div className={`w-5 h-5 bg-white rounded-full mt-0.5 ml-0.5 transition-transform ${
          checked ? 'translate-x-6' : ''
        }`}></div>
      </div>
    </label>
  )

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="relative inline-block mb-6">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl blur-lg opacity-50 animate-pulse"></div>
          <div className="relative w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl flex items-center justify-center shadow-2xl">
            <BarChart3 className="w-10 h-10 text-white" />
          </div>
        </div>
        <h2 className="text-4xl font-bold text-white mb-4">Critères de Tri</h2>
        <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
          Configurez les critères pour évaluer et classer les candidats
        </p>
      </div>

      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Critère d'âge */}
        <div className="glass-card p-6">
          <h4 className="text-white font-medium mb-4 flex items-center">
            <span className="text-2xl mr-3">🎂</span>
            Critère d'âge
          </h4>
          
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-300">Souhaitez-vous filtrer par âge ?</span>
            <ToggleSwitch 
              checked={criteria.ageImportant || false}
              onChange={(checked) => onCriteriaChange('ageImportant', checked)}
              color="blue"
            />
          </div>

          {criteria.ageImportant && (
            <div className="grid md:grid-cols-2 gap-4 mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div>
                <label className="block text-sm text-gray-300 mb-2">Âge minimum</label>
                <input
                  type="number"
                  min="18"
                  max="70"
                  value={criteria.ageMin || ''}
                  onChange={(e) => onCriteriaChange('ageMin', e.target.value)}
                  className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="18"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">Âge maximum</label>
                <input
                  type="number"
                  min="18"
                  max="70"
                  value={criteria.ageMax || ''}
                  onChange={(e) => onCriteriaChange('ageMax', e.target.value)}
                  className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="65"
                />
              </div>
              {errors.ageRange && (
                <div className="col-span-2 flex items-center p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
                  <span className="text-red-300 text-sm">{errors.ageRange}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Critère de genre */}
        <div className="glass-card p-6">
          <h4 className="text-white font-medium mb-4 flex items-center">
            <span className="text-2xl mr-3">⚧️</span>
            Critère de genre
          </h4>
          
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-300">Souhaitez-vous filtrer par genre ?</span>
            <ToggleSwitch 
              checked={criteria.genderImportant || false}
              onChange={(checked) => onCriteriaChange('genderImportant', checked)}
              color="purple"
            />
          </div>

          {criteria.genderImportant && (
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              {Object.entries(GENDER_OPTIONS).map(([key, label]) => (
                <label
                  key={key}
                  className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all hover:scale-105 ${
                    criteria.preferredGender === key
                      ? 'border-purple-500 bg-purple-500/10 shadow-lg'
                      : 'border-gray-600 hover:border-purple-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="gender"
                    checked={criteria.preferredGender === key}
                    onChange={() => onCriteriaChange('preferredGender', key)}
                    className="mr-3"
                  />
                  <span className="text-white">{label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Expérience professionnelle */}
        <div className="glass-card p-6">
          <h4 className="text-white font-medium mb-4 flex items-center">
            <span className="text-2xl mr-3">💼</span>
            Expérience professionnelle
          </h4>
          
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-300">L'expérience est-elle importante pour ce poste ?</span>
            <ToggleSwitch 
              checked={criteria.experienceImportant || false}
              onChange={(checked) => onCriteriaChange('experienceImportant', checked)}
              color="green"
            />
          </div>

          {criteria.experienceImportant && (
            <div className="space-y-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Expérience minimum (années)</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={criteria.expMin || ''}
                    onChange={(e) => onCriteriaChange('expMin', e.target.value)}
                    className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-green-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Expérience maximum (années)</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={criteria.expMax || ''}
                    onChange={(e) => onCriteriaChange('expMax', e.target.value)}
                    className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-green-500"
                    placeholder="10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">Type d'expérience recherchée</label>
                <select
                  value={criteria.experienceType || 'any'}
                  onChange={(e) => onCriteriaChange('experienceType', e.target.value)}
                  className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-green-500"
                >
                  <option value="any">Peu importe le domaine</option>
                  <option value="same-profile">Dans le même domaine</option>
                  <option value="related">Domaine connexe acceptable</option>
                </select>
              </div>

              {errors.expRange && (
                <div className="flex items-center p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
                  <span className="text-red-300 text-sm">{errors.expRange}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Formation et diplôme */}
        <div className="glass-card p-6">
          <h4 className="text-white font-medium mb-4 flex items-center">
            <span className="text-2xl mr-3">🎓</span>
            Formation et diplômes
          </h4>
          
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-300">Le niveau de formation est-il requis ?</span>
            <ToggleSwitch 
              checked={criteria.educationImportant || false}
              onChange={(checked) => onCriteriaChange('educationImportant', checked)}
              color="purple"
            />
          </div>

          {criteria.educationImportant && (
            <div className="space-y-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <div>
                <label className="block text-sm text-gray-300 mb-2">Niveau minimum requis</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(EDUCATION_LEVELS).map(([key, level]) => (
                    <label
                      key={key}
                      className={`flex flex-col items-center p-3 border rounded-lg cursor-pointer transition-all hover:scale-105 ${
                        criteria.minEducationLevel === key
                          ? 'border-purple-500 bg-purple-500/10 shadow-lg'
                          : 'border-gray-600 hover:border-purple-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="educationLevel"
                        checked={criteria.minEducationLevel === key}
                        onChange={() => onCriteriaChange('minEducationLevel', key)}
                        className="sr-only"
                      />
                      <span className="text-white font-semibold">{level.title}</span>
                      <span className="text-gray-400 text-xs text-center">{level.description}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">Domaine de formation (optionnel)</label>
                <select
                  value={criteria.educationField || ''}
                  onChange={(e) => onCriteriaChange('educationField', e.target.value)}
                  className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Tous domaines acceptés</option>
                  {Object.entries(EDUCATION_FIELDS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Nombre de sociétés */}
        <div className="glass-card p-6">
          <h4 className="text-white font-medium mb-4 flex items-center">
            <span className="text-2xl mr-3">🏢</span>
            Diversité d'expérience (nombre de sociétés)
          </h4>
          
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-300">Le nombre de sociétés fréquentées est-il important ?</span>
            <ToggleSwitch 
              checked={criteria.companiesCountImportant || false}
              onChange={(checked) => onCriteriaChange('companiesCountImportant', checked)}
              color="orange"
            />
          </div>

          {criteria.companiesCountImportant && (
            <div className="space-y-4 p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Nombre minimum de sociétés</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={criteria.minCompaniesCount || ''}
                    onChange={(e) => onCriteriaChange('minCompaniesCount', e.target.value)}
                    className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-orange-500"
                    placeholder="2"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Nombre maximum de sociétés</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={criteria.maxCompaniesCount || ''}
                    onChange={(e) => onCriteriaChange('maxCompaniesCount', e.target.value)}
                    className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-orange-500"
                    placeholder="10"
                  />
                </div>
              </div>
              
              <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                <p className="text-orange-300 text-sm">
                  💡 <strong>Conseil :</strong> Trop de sociétés peut indiquer de l'instabilité, 
                  trop peu peut manquer de diversité d'expérience.
                </p>
              </div>

              {errors.companiesRange && (
                <div className="flex items-center p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
                  <span className="text-red-300 text-sm">{errors.companiesRange}</span>
                </div>
              )}
            </div>
          )}
        </div>
        {/* === ENTREPRISES SPÉCIFIQUES === */}
<div className="glass-card p-6">
  <h4 className="text-white font-medium mb-4 flex items-center">
    <span className="text-2xl mr-3">🎯</span>
    Entreprises cibles spécifiques
  </h4>
  
  <div className="flex items-center justify-between mb-4">
    <span className="text-gray-300">Rechercher des candidats ayant travaillé dans des entreprises spécifiques ?</span>
    <ToggleSwitch 
      checked={criteria.targetCompaniesImportant || false}
      onChange={(checked) => onCriteriaChange('targetCompaniesImportant', checked)}
      color="pink"
    />
  </div>

  {criteria.targetCompaniesImportant && (
    <div className="space-y-4 p-4 bg-pink-500/10 border border-pink-500/20 rounded-lg">
      
      {/* Zone de saisie des entreprises */}
      <div>
        <label className="block text-sm text-gray-300 mb-2">
          Noms des entreprises recherchées
        </label>
        <textarea
          value={criteria.targetCompanies || ''}
          onChange={(e) => onCriteriaChange('targetCompanies', e.target.value)}
          rows={4}
          className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-pink-500 focus:outline-none resize-none"
          placeholder="Ex: Google, Microsoft, Apple, Amazon, Meta, Tesla, Netflix, Airbnb"
        />
        <div className="text-xs text-gray-400 mt-1">
          💡 Séparez les noms d'entreprises par des virgules. L'IA cherchera si le candidat a travaillé dans au moins une de ces sociétés.
        </div>
      </div>

      {/* Type de correspondance */}
      <div>
        <label className="block text-sm text-gray-300 mb-2">
          Type de correspondance
        </label>
        <select
          value={criteria.targetCompaniesMatchType || 'any'}
          onChange={(e) => onCriteriaChange('targetCompaniesMatchType', e.target.value)}
          className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-pink-500"
        >
          <option value="any">Au moins une entreprise de la liste</option>
          <option value="exact">Nom exact requis</option>
          <option value="partial">Correspondance partielle acceptée</option>
        </select>
        <div className="text-xs text-gray-400 mt-1">
          • <strong>Au moins une :</strong> Le candidat doit avoir travaillé dans au moins 1 entreprise de votre liste<br/>
          • <strong>Nom exact :</strong> Le nom doit correspondre exactement<br/>
          • <strong>Partielle :</strong> Correspondance partielle acceptée (ex: "Google France" pour "Google")
        </div>
      </div>

      {/* Bonus/Pénalité */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-300 mb-2">
            Points bonus si trouvé
          </label>
          <input
            type="number"
            min="0"
            max="30"
            value={criteria.targetCompaniesBonus || '10'}
            onChange={(e) => onCriteriaChange('targetCompaniesBonus', e.target.value)}
            className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-pink-500"
            placeholder="10"
          />
          <div className="text-xs text-gray-400">Points ajoutés si le candidat a travaillé dans une entreprise cible</div>
        </div>
        
        <div>
          <label className="block text-sm text-gray-300 mb-2">
            Pénalité si non trouvé
          </label>
          <input
            type="number"
            min="0"
            max="30"
            value={criteria.targetCompaniesPenalty || '5'}
            onChange={(e) => onCriteriaChange('targetCompaniesPenalty', e.target.value)}
            className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-pink-500"
            placeholder="5"
          />
          <div className="text-xs text-gray-400">Points retirés si aucune entreprise cible trouvée</div>
        </div>
      </div>

      {/* Aperçu */}
      {criteria.targetCompanies && (
        <div className="p-3 bg-pink-500/10 border border-pink-500/20 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-pink-400">🎯</span>
            <span className="text-pink-200 text-sm font-medium">
              Entreprises cibles configurées
            </span>
          </div>
          <div className="text-xs text-pink-300">
            <strong>Liste :</strong> {criteria.targetCompanies.split(',').map(c => c.trim()).slice(0, 5).join(', ')}
            {criteria.targetCompanies.split(',').length > 5 && ` (+${criteria.targetCompanies.split(',').length - 5} autres)`}
          </div>
          <div className="text-xs text-pink-300 mt-1">
            <strong>Bonus :</strong> +{criteria.targetCompaniesBonus || '10'} pts si trouvé • 
            <strong>Pénalité :</strong> -{criteria.targetCompaniesPenalty || '5'} pts si non trouvé
          </div>
        </div>
      )}
    </div>
  )}
</div>
      </div>

      <div className="flex justify-center space-x-4 pt-8">
        <GlowingButton variant="secondary" onClick={onPrev}>
          ← Retour
        </GlowingButton>
        <GlowingButton onClick={onNext}>
          Continuer →
        </GlowingButton>
      </div>
    </div>
  )
}

export default SortingStep